/**
 * React Query hooks for supervised generation.
 *
 * Two hook sets:
 * - Legacy session-based: useSupervisedSession, useApproveSection, etc.
 * - Content-based (P1.7): useSectionReview, useSectionAction, useSupervisedProgress
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSession,
  approveSection,
  editSection,
  regenerateSection as legacyRegenerateSection,
  completeSession,
  getSectionReview,
  submitSectionAction,
  getSupervisedProgress,
  editOutline,
  regenerateOutline,
  regenerateSectionOutline as regenSectionOutline,
  saveOutlineFeedback,
  getStrategyArtifact,
} from '@/services/supervisedGenerationService';
import type {
  SectionActionPayload,
  OutlineEditPayload,
  OutlineRegeneratePayload,
  SectionRegeneratePayload,
  OutlineFeedbackPayload,
} from '@/services/supervisedGenerationService';

const QUERY_KEYS = {
  session: 'supervised-session',
  sectionReview: 'supervised-section-review',
  progress: 'supervised-progress',
};

const POLLING_STATES = new Set(['generating', 'regenerating']);

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useSupervisedSession(sessionId: string | null) {
  return useQuery({
    queryKey: [QUERY_KEYS.session, sessionId],
    queryFn: () => getSession(sessionId!),
    enabled: !!sessionId,
    staleTime: 5_000,
    refetchInterval: (query) => {
      const state = query.state.data?.state?.current;
      if (state && POLLING_STATES.has(state)) return 5_000;
      return false;
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useApproveSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, sectionId }: { sessionId: string; sectionId: string }) =>
      approveSection(sessionId, sectionId),
    onSuccess: (_data, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.session, sessionId] });
    },
  });
}

export function useEditSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      sectionId,
      content,
    }: {
      sessionId: string;
      sectionId: string;
      content: Record<string, unknown>;
    }) => editSection(sessionId, sectionId, content),
    onSuccess: (_data, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.session, sessionId] });
    },
  });
}

export function useRegenerateSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, sectionId }: { sessionId: string; sectionId: string }) =>
      legacyRegenerateSection(sessionId, sectionId),
    onSuccess: (_data, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.session, sessionId] });
    },
  });
}

export function useCompleteSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId }: { sessionId: string }) =>
      completeSession(sessionId),
    onSuccess: (_data, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.session, sessionId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Content-based hooks (P1.7)
// ---------------------------------------------------------------------------

/**
 * Fetch the current section under review for a supervised article.
 * Polls every 3s when status indicates generation is in progress.
 */
export function useSectionReview(contentId: string | null, viewSection?: number | null) {
  return useQuery({
    queryKey: [QUERY_KEYS.sectionReview, contentId, viewSection ?? 'current'],
    queryFn: () => getSectionReview(contentId!, viewSection ?? undefined),
    enabled: !!contentId,
    staleTime: 3_000,
    refetchInterval: (query) => {
      // Don't poll if we have an error (e.g., 400 = not in review state yet)
      if (query.state.error) return false;
      const data = query.state.data;
      // Poll faster while outline is being generated
      if (data?.status === 'generating_outline') return 3_000;
      // Keep polling during outline_pending (slower) to detect worker completing approve
      if (data?.status === 'outline_pending') return 5_000;
      // Poll every 3s during section review (catches regeneration updates + section transitions)
      return 3_000;
    },
    retry: (failureCount, error) => {
      // Don't retry 400/404 — expected when content is generating
      const status = (error as any)?.response?.status;
      if (status === 400 || status === 404) return false;
      return failureCount < 3;
    },
  });
}

/**
 * Submit an action (approve/edit/regenerate) for the current section.
 * Invalidates section-review + progress queries on success.
 */
export function useSectionAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contentId,
      payload,
    }: {
      contentId: string;
      payload: SectionActionPayload;
    }) => submitSectionAction(contentId, payload),
    onSuccess: (_data, { contentId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.sectionReview, contentId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.progress, contentId] });
    },
  });
}

/**
 * Lightweight polling for supervised generation progress.
 * Used by the command center for status badges.
 */
export function useSupervisedProgress(contentId: string | null) {
  return useQuery({
    queryKey: [QUERY_KEYS.progress, contentId],
    queryFn: () => getSupervisedProgress(contentId!),
    enabled: !!contentId,
    staleTime: 5_000,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // Stop polling when completed or failed
      if (status === 'completed' || status === 'published' || status === 'failed') return false;
      return 5_000;
    },
  });
}

// ---------------------------------------------------------------------------
// Outline editing hooks
// ---------------------------------------------------------------------------

export function useOutlineEdit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contentId, payload }: { contentId: string; payload: OutlineEditPayload }) =>
      editOutline(contentId, payload),
    onSuccess: (_data, { contentId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.sectionReview, contentId] });
    },
  });
}

export function useOutlineRegenerate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contentId, payload }: { contentId: string; payload: OutlineRegeneratePayload }) =>
      regenerateOutline(contentId, payload),
    onSuccess: (_data, { contentId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.sectionReview, contentId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.progress, contentId] });
    },
  });
}

export function useSectionOutlineRegenerate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contentId, payload }: { contentId: string; payload: SectionRegeneratePayload }) =>
      regenSectionOutline(contentId, payload),
    onSuccess: (_data, { contentId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.sectionReview, contentId] });
    },
  });
}

export function useOutlineFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contentId, payload }: { contentId: string; payload: OutlineFeedbackPayload }) =>
      saveOutlineFeedback(contentId, payload),
    onSuccess: (_data, { contentId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.sectionReview, contentId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Generation Artifacts
// ---------------------------------------------------------------------------

export function useStrategyArtifact(contentId: string | null) {
  return useQuery({
    queryKey: ['strategy-artifact', contentId],
    queryFn: () => getStrategyArtifact(contentId!),
    enabled: !!contentId,
    staleTime: 60_000,
  });
}
